import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, XIcon, SparklesIcon, TrendingUpIcon, StarIcon, Maximize2Icon, Minimize2Icon, CheckCircle2Icon, StarHalfIcon } from 'lucide-react';
import { Tool } from '../data/tools';
interface ToolCardProps {
  tool: Tool;
}
function StarRating({
  rating
}: {
  rating: number;
}) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
  return <div className="flex items-center gap-1" title={`${rating} out of 5 stars`} data-id="element-74">
      <div className="flex items-center" data-id="element-75">
        {[...Array(fullStars)].map((_, i) => <StarIcon key={`full-${i}`} className="w-4 h-4 fill-amber-400 text-amber-400" data-id="element-76" />)}
        {hasHalfStar && <StarHalfIcon className="w-4 h-4 fill-amber-400 text-amber-400" data-id="element-77" />}
        {[...Array(emptyStars)].map((_, i) => <StarIcon key={`empty-${i}`} className="w-4 h-4 text-grey-300" data-id="element-78" />)}
      </div>
      <span className="text-sm font-semibold text-black ml-1" data-id="element-79">
        {rating.toFixed(1)}
      </span>
    </div>;
}
export function ToolCard({
  tool
}: ToolCardProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxOpen]);
  // Close on Escape key
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);
  const categoryColors: Record<string, string> = {
    Writing: 'bg-purple-100 text-purple-800',
    Design: 'bg-pink-100 text-pink-800',
    Video: 'bg-orange-100 text-orange-800',
    Code: 'bg-emerald-100 text-emerald-800',
    Audio: 'bg-cyan-100 text-cyan-800',
    Research: 'bg-amber-100 text-amber-800'
  };
  return <>
      <div className="group bg-white border border-grey-200 rounded-md p-6 hover:border-grey-400 transition-colors cursor-pointer flex flex-col h-full" onClick={() => setLightboxOpen(true)} role="button" tabIndex={0} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setLightboxOpen(true);
      }
    }} data-id="element-80">
        <div className="flex items-start justify-between mb-3" data-id="element-81">
          <h3 className="font-semibold text-lg text-black flex items-center gap-1.5" data-id="element-82">
            {tool.name}
            <BadgeCheckIcon className="w-4.5 h-4.5 text-white fill-black shrink-0" data-id="element-83" />
          </h3>
        </div>

        <p className="text-grey-600 text-sm mb-4 flex-grow" data-id="element-84">
          {tool.description}
        </p>

        <div className="flex flex-wrap items-center gap-1.5 mb-4" data-id="element-85">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100/60 text-grey-800" data-id="element-86">
            {tool.bestFor}
          </span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100/60 text-grey-800" data-id="element-87">
            {tool.pricing}
          </span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100/60 text-grey-800" data-id="element-88">
            {tool.ease}
          </span>
        </div>

        <div className="mt-auto flex items-center justify-between" data-id="element-89">
          <button onClick={e => {
          e.stopPropagation();
          setLightboxOpen(true);
        }} className="inline-flex items-center gap-2 text-sm text-white bg-black hover:bg-grey-800 px-4 py-2 rounded-full font-medium transition-colors" data-id="element-90">
            Visit
            <ArrowRightIcon className="w-4 h-4" data-id="element-91" />
          </button>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${tool.pricing === 'Free' ? 'bg-emerald-100 text-emerald-700' : tool.pricing === 'Freemium' ? 'bg-amber-100 text-amber-700' : 'bg-grey-100 text-grey-700'}`} data-id="element-92">
            {tool.pricing === 'Free' ? 'Free' : tool.pricing === 'Freemium' ? 'Free tier' : 'Paid'}
          </span>
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence data-id="element-93">
        {lightboxOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-94">
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setLightboxOpen(false)} data-id="element-95" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} className={`relative bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto z-10 transition-all duration-300 ${expanded ? 'max-w-4xl' : 'max-w-lg'}`} onClick={e => e.stopPropagation()} data-id="element-96">
              {/* Close & Expand Buttons */}
              <div className="absolute top-4 right-4 flex items-center gap-1 z-20" data-id="element-97">
                <button onClick={() => setExpanded(!expanded)} className="hidden sm:flex p-2 rounded-full hover:bg-grey-100 transition-colors" aria-label={expanded ? 'Collapse' : 'Expand'} data-id="element-98">
                  {expanded ? <Minimize2Icon className="w-5 h-5 text-grey-500" data-id="element-99" /> : <Maximize2Icon className="w-5 h-5 text-grey-500" data-id="element-100" />}
                </button>
                <button onClick={() => setLightboxOpen(false)} className="p-2 rounded-full hover:bg-grey-100 transition-colors" aria-label="Close" data-id="element-101">
                  <XIcon className="w-5 h-5 text-grey-500" data-id="element-102" />
                </button>
              </div>

              {/* Header with Logo */}
              <div className="p-4 sm:p-8 pb-0" data-id="element-103">
                <div className="flex items-start gap-4 sm:gap-5" data-id="element-104">
                  {/* Logo Placeholder */}
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-black text-white flex items-center justify-center text-xl sm:text-2xl font-bold shrink-0" data-id="element-105">
                    {tool.name.charAt(0)}
                  </div>

                  <div className="flex-1 min-w-0 pt-0 sm:pt-1" data-id="element-106">
                    <h2 className="text-xl sm:text-2xl font-bold text-black flex items-center gap-2 flex-wrap pr-8 sm:pr-0" data-id="element-107">
                      {tool.name}
                      <BadgeCheckIcon className="w-5 h-5 text-white fill-black shrink-0" data-id="element-108" />
                    </h2>
                    <p className="text-grey-500 text-sm mt-1" data-id="element-109">
                      {tool.category} Tool
                    </p>
                  </div>
                </div>

                {/* Status Badges */}
                {(tool.isNew || tool.featured || tool.trending) && <div className="flex flex-wrap gap-2 mt-4 sm:mt-5" data-id="element-110">
                    {tool.isNew && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-111">
                        <SparklesIcon className="w-3.5 h-3.5" data-id="element-112" />
                        New
                      </span>}
                    {tool.featured && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-113">
                        <StarIcon className="w-3.5 h-3.5" data-id="element-114" />
                        Featured
                      </span>}
                    {tool.trending && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700" data-id="element-115">
                        <TrendingUpIcon className="w-3.5 h-3.5" data-id="element-116" />
                        Trending
                      </span>}
                  </div>}
              </div>

              {/* Divider */}
              <div className="mx-4 sm:mx-8 my-5 sm:my-6 border-t border-grey-200" data-id="element-117" />

              {/* Description */}
              <div className="px-4 sm:px-8" data-id="element-118">
                <h3 className="text-sm font-semibold text-grey-400 uppercase tracking-wider mb-2" data-id="element-119">
                  About
                </h3>
                <AnimatePresence mode="wait" data-id="element-120">
                  {expanded || showFullDescription ? <motion.p key="long" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="text-grey-700 text-sm sm:text-base leading-relaxed" data-id="element-121">
                      {tool.longDescription || tool.description}
                    </motion.p> : <motion.p key="short" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="text-grey-700 text-sm sm:text-base leading-relaxed" data-id="element-122">
                      {tool.description}
                    </motion.p>}
                </AnimatePresence>
                {!expanded && tool.longDescription && <button onClick={() => setShowFullDescription(!showFullDescription)} className="mt-2 text-sm font-medium text-black hover:text-grey-600 transition-colors underline underline-offset-2" data-id="element-123">
                    {showFullDescription ? 'Show less' : 'Read more'}
                  </button>}
              </div>

              {/* Details Grid */}
              <div className="px-4 sm:px-8 mt-5 sm:mt-6" data-id="element-124">
                <h3 className="text-sm font-semibold text-grey-400 uppercase tracking-wider mb-3" data-id="element-125">
                  Details
                </h3>
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${expanded ? 'lg:grid-cols-4' : ''} gap-3`} data-id="element-126">
                  <div className="bg-grey-50 rounded-xl p-4" data-id="element-127">
                    <p className="text-xs text-grey-400 font-medium mb-1" data-id="element-128">
                      Category
                    </p>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${categoryColors[tool.category] || 'bg-grey-100 text-grey-800'}`} data-id="element-129">
                      {tool.category}
                    </span>
                  </div>
                  <div className="bg-grey-50 rounded-xl p-4" data-id="element-130">
                    <p className="text-xs text-grey-400 font-medium mb-1" data-id="element-131">
                      Best For
                    </p>
                    <p className="text-sm font-semibold text-black" data-id="element-132">
                      {tool.bestFor}
                    </p>
                  </div>
                  <div className="bg-grey-50 rounded-xl p-4" data-id="element-133">
                    <p className="text-xs text-grey-400 font-medium mb-1" data-id="element-134">
                      Pricing
                    </p>
                    <p className="text-sm font-semibold text-black" data-id="element-135">
                      {tool.pricing}
                    </p>
                  </div>
                  <div className="bg-grey-50 rounded-xl p-4" data-id="element-136">
                    <p className="text-xs text-grey-400 font-medium mb-1" data-id="element-137">
                      Difficulty
                    </p>
                    <p className="text-sm font-semibold text-black" data-id="element-138">
                      {tool.ease}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="px-4 sm:px-8 mt-5 sm:mt-6" data-id="element-139">
                <h3 className="text-sm font-semibold text-grey-400 uppercase tracking-wider mb-3" data-id="element-140">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-141">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100/60 text-grey-800" data-id="element-142">
                    {tool.bestFor}
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100/60 text-grey-800" data-id="element-143">
                    {tool.pricing}
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100/60 text-grey-800" data-id="element-144">
                    {tool.ease}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${categoryColors[tool.category] || 'bg-grey-100 text-grey-800'}`} data-id="element-145">
                    {tool.category}
                  </span>
                </div>
              </div>

              {/* Use Cases (Expanded Only) */}
              <AnimatePresence data-id="element-146">
                {expanded && tool.useCases && tool.useCases.length > 0 && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.3
            }} className="px-4 sm:px-8 mt-5 sm:mt-6 overflow-hidden" data-id="element-147">
                    <h3 className="text-sm font-semibold text-grey-400 uppercase tracking-wider mb-3" data-id="element-148">
                      How to use it day-to-day
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-149">
                      {tool.useCases.map((useCase, idx) => <div key={idx} className="flex items-start gap-3 bg-grey-50 rounded-xl p-4" data-id="element-150">
                          <CheckCircle2Icon className="w-5 h-5 text-black shrink-0 mt-0.5" data-id="element-151" />
                          <p className="text-sm text-grey-700 leading-relaxed" data-id="element-152">
                            {useCase}
                          </p>
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Actions */}
              <div className="px-4 sm:px-8 py-6 sm:py-8 mt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-6 border-t border-grey-100" data-id="element-153">
                <div className="flex-1 flex flex-col gap-1" data-id="element-154">
                  {tool.rating && <div className="flex items-center gap-2" data-id="element-155">
                      <StarRating rating={tool.rating} data-id="element-156" />
                      <span className="text-xs text-grey-500 font-medium" data-id="element-157">
                        Based on Google & App Store reviews
                      </span>
                    </div>}
                </div>
                <a href={tool.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 text-sm text-white bg-black hover:bg-grey-800 px-8 py-3 rounded-full font-semibold transition-colors shrink-0" data-id="element-158">
                  Visit {tool.name}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-159" />
                </a>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}